import React from 'react';
import { Checkbox, Icon } from 'Components';
import classNames from 'classnames';
import { Assignment } from 'LearningManager/constants/CoursesAssignment.types';
import styles from './AssignmentCard.module.scss';
import DropdownMenu from '../Dropdown';

interface AssignmentCardProps {
	assignment: Assignment;
	checked?: boolean;
	menuDisabled?: boolean;
	onCheck?: (id: string, checked: boolean) => void;
	onViewContent?: (id: string) => void;
	onMenuAction?: (id: string, action: string) => void;
}

const AssignmentCard: React.FC<AssignmentCardProps> = ({
	assignment,
	checked = false,
	menuDisabled = false,
	onCheck,
	onViewContent,
	onMenuAction
}) => {
	const {
		id,
		name,
		type,
		status,
		courseCount,
		completionPercentage = 35
	} = assignment;

	const courseLabel = `${courseCount} ${
		courseCount === 1 ? 'Course' : 'Courses'
	}`;

	return (
		<div className={styles.card}>
			<Checkbox
				className={styles.checkbox}
				checked={checked}
				onChange={(e) => onCheck?.(id, e.target.checked)}
			/>

			<div className={styles.body}>
				<div className={styles.name}>{name}</div>

				<div className={styles.tags}>
					<span
						className={classNames(styles.tag, {
							[styles['tag--oneTime']]: type === 'One-Time',
							[styles['tag--ongoing']]: type === 'Ongoing'
						})}
					>
						{type}
					</span>

					{status === 'Scheduled' && (
						<span
							className={classNames(
								styles.tag,
								styles['tag--scheduled']
							)}
						>
							Scheduled
						</span>
					)}
				</div>

				<div className={styles.courseCount}>{courseLabel}</div>
			</div>

			<div className={styles.progressBar}>
				<div
					className={styles.progressFill}
					style={{ width: `${Math.min(completionPercentage, 100)}%` }}
				/>
			</div>

			<div className={styles.actions}>
				<DropdownMenu
					arrow={false}
					disabled={menuDisabled}
					onChange={(key: string) => onMenuAction?.(id, key)}
					options={[
						{ key: 'view-details', label: 'View details' },
						{ key: 'edit', label: 'Edit' },
						...(status === 'Scheduled'
							? [
									{
										key: 'reset-completion',
										label: 'Reset completion'
									}
							  ]
							: []),
						{ key: 'unassign', label: 'Unassign', danger: true }
					]}
				>
					<div
						className={classNames(styles.moreBtn, {
							[styles['moreBtn--disabled']]: menuDisabled
						})}
					>
						<Icon type="optionIcon" role="img" />
					</div>
				</DropdownMenu>

				<span
					className={styles.viewContent}
					onClick={() => onViewContent?.(id)}
				>
					View content <Icon type="chevronDown" role="img" />
				</span>
			</div>
		</div>
	);
};

export default AssignmentCard;
